import { useAuth } from '../../context/AuthContext';
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import { InlineLoader } from '../../components/common/Loader';
import {
  generateSlots,
  getMySlots,
  updateSlot,
  updateSlotDelay,
  deleteSlot,
  deleteBulkSlots,
} from '../../services/doctorService';
import { useSocket } from '../../context/SocketContext';
import { formatTime12, formatDate, todayISO } from '../../utils/helpers';

// ── Generate slots modal ──────────────────────────────────────────────────
function GenerateSlotsModal({ onClose, onGenerated }) {
  const [form, setForm] = useState({
    startTime:   '09:00',
    endTime:     '13:00',
    duration:    10,
    maxPatients: 1,
    type:        'clinic',
  });
  const [selectedDates, setSelectedDates] = useState([todayISO()]);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Next 31 days for multi-select
  const next7 = Array.from({ length: 31 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d.toISOString().split('T')[0];
  });

  const toggleDate = (date) => {
    setSelectedDates(p =>
      p.includes(date) ? p.filter(d => d !== date) : [...p, date]
    );
  };

  const handleGenerate = async () => {
    if (!selectedDates.length) { toast.error('Select at least one date'); return; }
    if (form.startTime >= form.endTime) { toast.error('End time must be after start time'); return; }
    setLoading(true);
    try {
      const res = await generateSlots({
        dates:       selectedDates,
        startTime:   form.startTime,
        endTime:     form.endTime,
        duration:    Number(form.duration),
        maxPatients: Number(form.maxPatients),
        type:        form.type,
      });
      toast.success(`${res.created} slot(s) created!`);
      onGenerated();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="bottom-sheet p-5 space-y-4">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto" />
        <h3 className="text-base font-bold text-text-primary font-heading">Generate Slots</h3>

        {/* Type toggle */}
        <div>
          <label className="input-label">Slot Type</label>
          <div className="flex gap-2">
            {['clinic', 'video'].map(t => (
              <button key={t} onClick={() => set('type', t)}
                className={`flex-1 py-2.5 rounded-btn border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  form.type === t ? 'border-primary bg-primary-light text-primary' : 'border-border text-text-secondary'
                }`}>
                <i className={`fa-solid ${t === 'clinic' ? 'fa-hospital' : 'fa-video'} text-sm`} />
                {t === 'clinic' ? 'Clinic Visit' : 'Video Consult'}
              </button>
            ))}
          </div>
        </div>

        {/* Date multi-select */}
        <div>
          <label className="input-label">Select Dates</label>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
            {next7.map(d => {
              const dt    = new Date(d);
              const day   = dt.toLocaleDateString('en', { weekday: 'short' });
              const num   = dt.getDate();
              const sel   = selectedDates.includes(d);
              return (
                <button key={d} onClick={() => toggleDate(d)}
                  className={`flex-shrink-0 flex flex-col items-center px-3 py-2 rounded-xl border transition-all ${
                    sel ? 'border-primary bg-primary text-white' : 'border-border bg-white text-text-primary'
                  }`}>
                  <span className={`text-[9px] ${sel ? 'text-white/80' : 'text-text-muted'}`}>{day}</span>
                  <span className="text-sm font-bold">{num}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Time range */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="input-label">Start Time</label>
            <input type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)} className="input" />
          </div>
          <div className="flex-1">
            <label className="input-label">End Time</label>
            <input type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)} className="input" />
          </div>
        </div>

        {/* Duration + max patients */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="input-label">Duration (mins)</label>
            <select value={form.duration} onChange={e => set('duration', e.target.value)} className="input">
              {[5,10,15,20,30,45,60].map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="input-label">Max Patients</label>
            <input type="number" min={1} max={10} value={form.maxPatients}
              onChange={e => set('maxPatients', e.target.value)} className="input" />
          </div>
        </div>

        <button onClick={handleGenerate} disabled={loading} className="btn-primary">
          {loading
            ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Generating...</span>
            : <span><i className="fa-solid fa-wand-magic-sparkles mr-2" />Generate Slots</span>}
        </button>
      </div>
    </>
  );
}

// ── Slot card ─────────────────────────────────────────────────────────────
function SlotCard({ slot, onDelayUpdate, onDelete, onToggle }) {
  const [editDelay, setEditDelay] = useState(false);
  const [delay, setDelay]         = useState(slot.delayMinutes || 0);
  const [saving, setSaving]       = useState(false);

  const isFull    = slot.bookedCount >= slot.maxPatients;
  const isExpired = slot.isExpired === true;

  const saveDelay = async () => {
    setSaving(true);
    try {
      await onDelayUpdate(slot._id, Number(delay));
      setEditDelay(false);
      toast.success('Delay updated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`border rounded-xl p-3 transition-all ${
      isExpired ? 'bg-slate-50 border-slate-200 opacity-65' :
      !slot.isActive ? 'bg-gray-50 border-gray-200 opacity-60' :
      isFull ? 'bg-orange-50 border-orange-200' :
      'bg-white border-border'
    }`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-text-primary">{formatTime12(slot.startTime)}</p>
            {isExpired && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500 flex items-center gap-1">
                <i className="fa-solid fa-clock-rotate-left text-[8px]" /> Expired
              </span>
            )}
          </div>
          <p className="text-xs text-text-muted">
            {slot.duration}min ·
            <span className={`ml-1 font-medium ${isFull ? 'text-danger' : 'text-success'}`}>
              {slot.bookedCount}/{slot.maxPatients} booked
            </span>
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5">
          {slot.delayMinutes > 0 && !editDelay && (
            <span className="badge-warning text-[10px]">
              <i className="fa-solid fa-clock mr-1 text-[9px]" />{slot.delayMinutes}m delay
            </span>
          )}
          {!isExpired && slot.isActive && !isFull && (
            <button onClick={() => setEditDelay(!editDelay)} className="btn-icon w-7 h-7" title="Set delay">
              <i className="fa-solid fa-clock text-accent text-xs" />
            </button>
          )}
          {!isExpired && (
            <button onClick={() => onToggle(slot._id, !slot.isActive)} className="btn-icon w-7 h-7"
              title={slot.isActive ? 'Disable' : 'Enable'}>
              <i className={`fa-solid ${slot.isActive ? 'fa-eye' : 'fa-eye-slash'} text-text-muted text-xs`} />
            </button>
          )}
          <button onClick={() => onDelete(slot._id)} className="btn-icon w-7 h-7" title="Delete slot">
            <i className="fa-solid fa-trash text-danger text-xs" />
          </button>
        </div>
      </div>

      {/* Inline delay editor */}
      {editDelay && (
        <div className="mt-2 pt-2 border-t border-border">
          <p className="text-xs text-text-muted mb-2">Set delay in minutes:</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="number" min={0} max={120}
              value={delay}
              onChange={e => setDelay(e.target.value)}
              className="input py-1.5 text-sm flex-1 min-w-0"
              style={{ maxWidth: '100px' }}
            />
            <button onClick={saveDelay} disabled={saving}
              className="flex-1 py-2 bg-primary text-white text-xs font-bold rounded-xl">
              {saving ? <i className="fa-solid fa-spinner animate-spin" /> : <><i className="fa-solid fa-check mr-1" />Save Delay</>}
            </button>
            <button onClick={() => setEditDelay(false)}
              className="flex-1 py-2 border border-border text-xs rounded-xl text-text-secondary font-medium">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DoctorSlots() {
  const { role, user } = useAuth();


  const { socket }     = useSocket();
  const [activeType, setActiveType] = useState('clinic');
  const [selDate, setSelDate]       = useState(todayISO());
  const [slots, setSlots]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);

  const loadSlots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMySlots({ date: selDate, type: activeType });
      setSlots(res.slots || []);
    } catch (err) {
      console.error('[DoctorSlots] loadSlots error:', err?.message || err);
      toast.error(err?.message || 'Failed to load slots');
    } finally {
      setLoading(false);
    }
  }, [selDate, activeType]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const handleDelayUpdate = async (slotId, delayMinutes) => {
    await updateSlotDelay(slotId, delayMinutes);
    // Emit socket event for real-time patient notification
    socket?.emit('update-slot-delay', { slotId, delayMinutes });
    setSlots(p => p.map(s => s._id === slotId ? { ...s, delayMinutes } : s));
  };

  const handleDelete = async (slotId) => {
    if (!window.confirm('Delete this slot?')) return;
    try {
      await deleteSlot(slotId);
      toast.success('Slot deleted');
      setSlots(p => p.filter(s => s._id !== slotId));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleToggle = async (slotId, isActive) => {
    try {
      await updateSlot(slotId, { isActive });
      setSlots(p => p.map(s => s._id === slotId ? { ...s, isActive } : s));
      toast.success(isActive ? 'Slot enabled' : 'Slot disabled');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete all ${activeType} slots for ${formatDate(selDate)}?`)) return;
    try {
      await deleteBulkSlots({ date: selDate, type: activeType });
      toast.success('All slots deleted');
      setSlots([]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Next 31 days continuous — no gaps
  const dates = Array.from({ length: 31 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d.toISOString().split('T')[0];
  });


  // ── Staff permission guard (must be AFTER all hooks) ──────────────────
  if (role === 'staff' && !user?.permissions?.['slotManagement']) {
    return (
      <div className="page-container flex flex-col items-center justify-center py-20 gap-4 text-center px-8">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
          <i className="fa-solid fa-lock text-red-500 text-3xl" />
        </div>
        <h2 className="text-lg font-bold text-text-primary">Access Restricted</h2>
        <p className="text-sm text-text-muted leading-relaxed">
          You don&apos;t have permission to access this section.<br />
          Contact your doctor to enable it.
        </p>
        <button onClick={() => window.history.back()}
          className="px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-bold">
          Go Back
        </button>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-surface pb-6">
      <Header title="Slot Management" showBack />

      <div className="pt-14">
        {/* Type toggle */}
        <div className="bg-white border-b border-border flex">
          {['clinic', 'video'].map(t => (
            <button key={t} onClick={() => setActiveType(t)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold border-b-2 transition-all ${
                activeType === t ? 'border-primary text-primary' : 'border-transparent text-text-muted'
              }`}>
              <i className={`fa-solid ${t === 'clinic' ? 'fa-hospital' : 'fa-video'} text-sm`} />
              {t === 'clinic' ? 'Clinic' : 'Video'}
            </button>
          ))}
        </div>

        {/* Date selector */}
        <div className="bg-white border-b border-border px-4 py-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {dates.map(d => {
              const dt  = new Date(d);
              const day = dt.toLocaleDateString('en', { weekday: 'short' });
              const num = dt.getDate();
              const sel = selDate === d;
              return (
                <button key={d} onClick={() => setSelDate(d)}
                  className={`flex-shrink-0 flex flex-col items-center px-3 py-2 rounded-xl border transition-all ${
                    sel ? 'border-primary bg-primary text-white' : 'border-border bg-white text-text-primary'
                  }`}>
                  <span className={`text-[9px] ${sel ? 'text-white/80' : 'text-text-muted'}`}>{day}</span>
                  <span className="text-sm font-bold">{num}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-4 pt-3">
          {/* Header row */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-text-primary">
                {formatDate(selDate)} · {slots.length} slot{slots.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-text-muted">
                {slots.filter(s => s.bookedCount > 0).length} booked,{' '}
                {slots.filter(s => s.isActive && s.bookedCount < s.maxPatients).length} available
              </p>
            </div>
            <div className="flex gap-2">
              {slots.length > 0 && (
                <button onClick={handleDeleteAll}
                  className="px-3 py-1.5 border border-danger/30 text-danger text-xs font-medium rounded-lg">
                  <i className="fa-solid fa-trash mr-1 text-[10px]" />Clear All
                </button>
              )}
              <button onClick={() => setShowGenerate(true)}
                className="px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-lg">
                <i className="fa-solid fa-plus mr-1 text-[10px]" />Add Slots
              </button>
            </div>
          </div>

          {/* Slots */}
          {loading ? <InlineLoader text="Loading slots..." /> :
           slots.length === 0 ? (
            <div className="text-center py-12">
              <i className="fa-solid fa-clock text-3xl text-text-muted mb-3 block" />
              <p className="text-sm text-text-muted mb-3">No slots for this date</p>
              <button onClick={() => setShowGenerate(true)}
                className="px-6 py-2.5 bg-primary text-white text-sm font-semibold rounded-btn">
                <i className="fa-solid fa-plus mr-2" />Generate Slots
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {slots.map(slot => (
                <SlotCard
                  key={slot._id}
                  slot={slot}
                  onDelayUpdate={handleDelayUpdate}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showGenerate && (
        <GenerateSlotsModal
          onClose={() => setShowGenerate(false)}
          onGenerated={loadSlots}
        />
      )}
    </div>
  );
}
